import { ref } from "@vue/composition-api";
import { assistantAPI, giftAPI } from "../utils/api.js";
import { storage } from "../utils/storage.js";
import { ph, phSetSession, phClearSession } from "../utils/posthog.js";
import { trackUserEvent } from "../utils/userEvents.js";
import {
  createCartesiaStream,
  cancelCartesiaTTS,
  closeCartesiaTransport,
  warmCartesia,
} from "../utils/cartesiaService.js";

/**
 * Full session lifecycle against govava-new-core + direct WSS to OpenAI
 * Realtime. Covers: mint, WSS connect + session.update + context replay,
 * mic capture (Web Audio, PCM16 @ 24kHz), inbound audio playback, token
 * refresh, tool-call routing (search_gifts / end_conversation), context
 * save after each turn, finalize on end.
 */

const REALTIME_WSS_BASE = "wss://api.openai.com/v1/realtime";
const SAMPLE_RATE = 24000;
const ECHO_FILLER_PRIMARY = "Let me find the best gifts for you — one moment.";
const ECHO_FILLER_SLOW = "Taking a bit longer, almost there.";
const SLOW_SEARCH_MS = 2500;

// Sentence chunking for streaming TTS. The first chunk of a turn is allowed to
// be short so Ava starts speaking as early as possible; later chunks are longer
// so we are not paying a request round-trip per clause.
const TTS_FIRST_CHUNK_MIN_CHARS = 16;
const TTS_CHUNK_MIN_CHARS = 40;
const TTS_SOFT_BREAK_MIN_CHARS = 70; // only break on , ; : past this length
const TTS_CHUNK_MAX_CHARS = 200; // hard cut for run-on output

// Fixed part of the echo-guard tail: how long Ava's last chunk keeps arriving
// at the user's ears AFTER Anam has received it — WebRTC jitter buffer, decode,
// output device latency and room reverb. The variable part (audio we have sent
// that Anam has not finished playing) is computed per turn.
const ECHO_GUARD_PIPELINE_MS = 700;

// How far back we compare a user transcript against Ava's own speech, and how
// long after she stops that comparison stays armed.
const ECHO_TRANSCRIPT_WINDOW_MS = 30000;
const ECHO_TRANSCRIPT_WINDOW_TAIL_MS = 1500;

// Cap on how far ahead we will schedule fallback Web Audio playback. Without
// it a long turn grows an unbounded queue of scheduled buffers.
const MAX_PLAYBACK_QUEUE_SEC = 10;

// How far ahead of real time we are willing to push audio into Anam's data
// channel. Cartesia generates several times faster than real time, so without
// a cap a single sentence arrives as one large burst that competes with the
// avatar's own video RTP stream — which is what shows up as video stutter.
// Metering it keeps the channel calm AND makes barge-in tighter: audio we
// never sent is audio Anam never has to purge.
const AVATAR_LEAD_MS = 300;
const AVATAR_PUMP_TICK_MS = 20;

// Emotion vocabulary. These are exactly the runtime cue tags the Anam engine
// accepts (DIRECTOR_NOTE_CUE_TAGS in @anam-ai/js-sdk); anything else is
// rejected by the SDK before it reaches the wire.
const EMOTION_TAGS = [
  "happy",
  "warm",
  "playful",
  "laughter",
  "curious",
  "supportive",
  "concerned",
  "sad",
  "surprised",
  "angry",
  "distressed",
];
// Longest plausible "[tag]" span, used to decide whether a trailing "[" is a
// tag still arriving or just a literal bracket.
const MAX_EMOTION_TAG_SPAN = 14;

/**
 * Appended to the Laravel-supplied system prompt. Two jobs: make her WRITE
 * like someone talking rather than someone typing (which is most of what
 * "sounds robotic" actually is), and have her mark tone shifts so the avatar's
 * face can follow. Tags are stripped before anything is spoken or stored.
 */
const PERFORMANCE_DIRECTION = `--- SPEAKING STYLE ---
Everything you write is spoken aloud by a warm, friendly presenter and
performed by an on-screen avatar. Write for the ear, never for the page:
- Use contractions always: "I'll", "that's", "you're", "let's".
- Vary your rhythm. A short sentence lands. Then a longer one carries detail.
- React before you inform: "Ooh, nice one.", "Got it.", "Oh, that's lovely."
- Keep replies short - usually one or two sentences - and ask ONE question at a
  time, then stop and let them answer.
- Never output markdown, bullet points, emoji, URLs, IDs, or stage directions.

--- PACE OF THE CONVERSATION ---
Follow the conversation flow in your instructions above in order. Do not skip
ahead: never call search_gifts until you have actually asked for, and been
given, the details those instructions tell you to collect. If you are unsure
what the person wants, ask - do not guess and search.
Only ever respond to what the person actually said. If a message appears to be
your own words repeated back, or is empty or nonsensical, do not answer it -
briefly re-ask your last question instead.

--- EMOTION TAGS ---
When your tone genuinely shifts, start that sentence with ONE tag:
[warm] [happy] [playful] [curious] [supportive] [concerned] [surprised]
Example: "[happy] Ooh, she'll love that. [curious] What's your budget?"
At most one tag per sentence and only a couple per reply. The tag is removed
before you are heard - it drives the avatar's expression, so it must honestly
match what you are saying at that moment.`;

let cachedPCM16Buf = new Int16Array(4096);

function floatToPCM16(float32) {
  if (cachedPCM16Buf.length < float32.length) {
    cachedPCM16Buf = new Int16Array(float32.length);
  }
  for (let i = 0; i < float32.length; i++) {
    const s = Math.max(-1, Math.min(1, float32[i]));
    cachedPCM16Buf[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return new Int16Array(cachedPCM16Buf.buffer, 0, float32.length);
}

/**
 * Base64 an ArrayBuffer or a typed-array VIEW. Taking a view is the point:
 * floatToPCM16 hands back a window onto a larger reused buffer, and reading
 * `.buffer` off it (as this used to) serialises the whole cache — trailing
 * stale samples included — into the mic stream.
 */
function bufToBase64(buffer) {
  const bytes =
    buffer instanceof ArrayBuffer
      ? new Uint8Array(buffer)
      : new Uint8Array(buffer.buffer, buffer.byteOffset, buffer.byteLength);
  let binary = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(
      null,
      bytes.subarray(i, Math.min(i + CHUNK, bytes.length)),
    );
  }
  return btoa(binary);
}

/**
 * How many characters of `text` form the next natural thing to speak, or 0 if
 * we should keep buffering.
 *
 * We break on sentence punctuation as soon as the segment is long enough to
 * sound like a phrase, fall back to clause punctuation once the segment is
 * getting long, and hard-cut at a word boundary if the model produces a
 * run-on. Decimals ("3.5") and initialisms ("U.S.") are not breaks.
 */
function speakableChunkLength(text, minChars) {
  let softBreak = 0;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    const strong = c === "." || c === "!" || c === "?" || c === "…";
    const newline = c === "\n";
    const soft = c === "," || c === ";" || c === ":" || c === "—";
    if (!strong && !soft && !newline) continue;

    if (c === ".") {
      const prev = text[i - 1];
      const next = text[i + 1];
      if (prev && next && /\d/.test(prev) && /\d/.test(next)) continue;
      const prev2 = text[i - 2];
      if (prev && /[A-Za-z]/.test(prev) && (!prev2 || !/[A-Za-z]/.test(prev2))) {
        continue; // single letter before the dot -> initialism
      }
    }

    // Swallow a closing quote/bracket, then require whitespace or end-of-text
    // so we do not split inside a token.
    let end = i + 1;
    while (end < text.length && /["')\]]/.test(text[end])) end++;
    const after = text[end];
    if (!newline && after !== undefined && !/\s/.test(after)) continue;

    if (end < minChars) continue;
    if (strong || newline) return end;
    if (end >= TTS_SOFT_BREAK_MIN_CHARS) softBreak = end;
  }

  if (softBreak) return softBreak;

  if (text.length >= TTS_CHUNK_MAX_CHARS) {
    const cut = text.lastIndexOf(" ", TTS_CHUNK_MAX_CHARS);
    if (cut >= minChars) return cut + 1;
  }
  return 0;
}

function base64ToBytes(b64) {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

function pcm16ToFloat32(bytes) {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const out = new Float32Array(bytes.byteLength / 2);
  for (let i = 0; i < out.length; i++) {
    const s = view.getInt16(i * 2, true);
    out[i] = s < 0 ? s / 0x8000 : s / 0x7fff;
  }
  return out;
}

export function useAssistantSession() {
  // -- session state (from Laravel) ----------------------------------------
  const sessionId = ref(null);
  const ephemeralToken = ref(null);
  const expiresAt = ref(null);
  const model = ref(null);
  const voice = ref(null);
  const avatarId = ref(null);
  const anamSessionToken = ref(null);
  const systemPrompt = ref(null);
  const tools = ref([]);
  const contextMessages = ref([]);

  // Target DOM element the Anam SDK streams the avatar video into.
  // The component sets this via setAvatarVideoElementId(id) before calling
  // startSession; if not set, the session still works but without a video.
  let avatarVideoElementId = null;
  let anamClient = null;
  let anamAudioStream = null;
  // Guard so a CONNECTION_CLOSED storm (e.g. server churn) doesn't
  // trigger overlapping reconnect attempts.
  let anamReconnectInProgress = false;

  // -- runtime state --------------------------------------------------------
  const messages = ref([]); // full running transcript for Laravel
  const isActive = ref(false);
  const isConnected = ref(false);
  const isStarting = ref(false);
  const isMuted = ref(false);
  const isReplicaSpeaking = ref(false);
  const useCartesiaTTSMode = ref(false);
  let replicaTurnAudioSamples = 0;
  let replicaTurnStartTs = 0;
  let replicaSpeakingTimer = null;
  const error = ref(null);
  const gifts = ref([]);
  const isFetchingGifts = ref(false);

  // -- Listening Reflex ("Hmm..." Audio Acknowledgement) ---------------------
  // let hmmAudio = null;
  // let hmmTimer = null;
  // let lastHmmTime = 0;
  // const HMM_TRIGGER_DELAY_MS = 2200; // Trigger "Hmm..." after 2.2s of sustained user speech
  // const HMM_COOLDOWN_MS = 6000; // Cooldown 6s between "Hmm..." reflex acknowledgements

  // function triggerHmmReflex() {
  //   if (hmmTimer) {
  //     clearTimeout(hmmTimer);
  //     hmmTimer = null;
  //   }
  //   const now = Date.now();
  //   if (now - lastHmmTime < HMM_COOLDOWN_MS) return;

  //   hmmTimer = setTimeout(() => {
  //     if (isReplicaSpeaking.value || isFetchingGifts.value) return;
  //     lastHmmTime = Date.now();
  //     try {
  //       if (!hmmAudio) {
  //         hmmAudio = new Audio("/audio/cartesia_hmm_audio.wav");
  //         hmmAudio.volume = 0.65;
  //       }
  //       hmmAudio.currentTime = 0;
  //       hmmAudio.play().catch((err) => {
  //         console.warn("Hmm audio play deferred/blocked:", err);
  //       });
  //     } catch (e) {
  //       console.warn("Failed to play hmm audio:", e);
  //     }
  //   }, HMM_TRIGGER_DELAY_MS);
  // }

  // function stopHmmReflex() {
  //   if (hmmTimer) {
  //     clearTimeout(hmmTimer);
  //     hmmTimer = null;
  //   }
  //   if (hmmAudio) {
  //     try {
  //       hmmAudio.pause();
  //       hmmAudio.currentTime = 0;
  //     } catch (_) {}
  //   }
  // }

  // -- internals ------------------------------------------------------------
  let ws = null;
  // Every socket's listeners capture the generation they were bound under.
  // A stale socket's late `close` event used to clear isConnected AFTER the
  // replacement socket had already opened, which silently wedged the mic for
  // the rest of the session.
  let wsGeneration = 0;
  // Bumped by startSession/teardown so async work from an old session (the
  // greeting fallback, an Anam reconnect, a queued save) cannot touch a new one.
  let sessionGeneration = 0;
  let refreshTimer = null;
  let greetingFallbackTimer = null;
  let audioCtx = null;
  let micStream = null;
  let micProcessor = null;
  let micWorkletNode = null;
  let micSource = null;
  let playbackCtx = null;
  let playbackQueueTime = 0;
  let playbackSources = new Set();
  let currentUserTurn = "";
  let currentAssistantTurn = "";
  let onToolCallCallbacks = [];

  // -- assistant turn ownership ---------------------------------------------
  // The single authority over "is this audio still wanted". Incremented on
  // every barge-in and on every session (re)start. Cartesia stream callbacks
  // capture the id they were created under and compare before touching Anam,
  // so a cancelled response's audio can never arrive late. This is a plain
  // counter on purpose: AbortController and timers are both best-effort, and
  // neither can stop a callback that is already on the microtask queue.
  let activeTurnId = 0;
  let ttsStream = null;
  let ttsPendingText = "";
  let ttsChunksSent = 0;
  let responseInProgress = false;
  // The response OpenAI is currently producing, and whether the user has
  // walked away from it. `response.cancel` stops generation server-side but
  // deltas already on the wire still arrive — without this they would refill
  // the TTS buffer and Ava would resume the sentence she was interrupted in.
  let currentResponseId = null;
  let assistantTurnAbandoned = false;

  // Metered delivery of TTS audio into Anam.
  let avatarQueue = [];
  let avatarQueuedMs = 0;
  let avatarSentMs = 0;
  let avatarSequenceStartedAt = 0;
  let avatarPumpTimer = null;
  let avatarEndPending = false;

  // Expression state. `ttsRawBuffer` holds text that may still contain a
  // partially-arrived "[tag]"; nothing leaves it until we know.
  let ttsRawBuffer = "";
  let pendingEmotion = null;
  let activeEmotion = null;
  let directorCuesSupported = true;
  let directorCueFailures = 0;

  // Rolling record of what Ava actually said, used to recognise her own voice
  // coming back through the microphone.
  let recentAssistantSpeech = [];
  let lastAssistantAudioEndedAt = 0;
  let echoGuardTimer = null;
  let echoLearnTimer = null;
  // Output modality for the Realtime session. With Cartesia doing the voice,
  // OpenAI's own audio is decoded and thrown away — so we ask for text only,
  // which is both cheaper and materially faster to first token. Flipped back
  // automatically if the API rejects the field.
  let textOnlyOutput = true;
  let sessionUpdateFellBack = false;
  // Greeting state is per-startSession, NOT per-connect. Token refresh calls
  // connect() but must not re-fire the greeting.
  let greetingFired = false;
  let micUnlocked = false;
  // Track Anam's "finished speaking" pulses so we can wait for the avatar
  // audio queue to actually drain before tearing down on end_conversation.
  let anamLastSpeechFinishedAt = 0;
  let anamSpeechTurnCount = 0;
  let anamSpeechResolvers = [];
  // Web Audio AnalyserNode wired to Anam's output MediaStream so we can
  // detect when the avatar has actually finished playing audio (Anam SDK in
  // passthrough mode does NOT emit a "playback drained" event — verified
  // against their docs and the bundled SDK source). We poll RMS levels and
  // declare silence after a continuous quiet window. This is what
  // runEndConversation waits on before tearing down.
  let anamAnalyserCtx = null;
  let anamAnalyser = null;
  let anamAnalyserData = null;
  // Two-sided readiness: greeting fires only when both OpenAI session.updated
  // AND Anam SESSION_READY have arrived. Otherwise audio chunks race ahead
  // of Anam being ready and get silently dropped.
  let openaiSessionReady = false;
  let anamSessionReady = false;

  function onToolCall(fn) {
    onToolCallCallbacks.push(fn);
  }
  function emitToolCall(payload) {
    onToolCallCallbacks.forEach((fn) => {
      try {
        fn(payload);
      } catch (e) {
        console.warn(e);
      }
    });
  }

  function maybeFireGreeting() {
    if (greetingFired) return;
    if (!openaiSessionReady) return;
    if (!anamSessionReady && anamSessionToken.value) {
      // Wait for Anam unless we're running without an avatar.
      return;
    }
    greetingFired = true;
    console.log("[assistant] firing greeting kick");
    sendWS({
      type: "conversation.item.create",
      item: {
        type: "message",
        role: "user",
        content: [{ type: "input_text", text: "hi" }],
      },
    });
    sendWS({ type: "response.create" });
  }

  // -- Laravel lifecycle ----------------------------------------------------

  async function startSession() {
    if (isStarting.value || isActive.value) return;
    isStarting.value = true;
    error.value = null;
    try {
      const data = await assistantAPI.startSession();
      if (!data) throw new Error("Empty response from /assistant/start");
      applySessionPayload(data);
      messages.value = [];
      gifts.value = [];
      isActive.value = true;
      phSetSession(data.session_id);
      ph("assistant_session_started", {
        session_id: data.session_id,
        model: data.model,
        voice: data.voice,
        avatar_id: data.avatar_id,
        prior_message_count: Array.isArray(data.context_messages)
          ? data.context_messages.length
          : 0,
      });
      // Reset per-session flags BEFORE we open WSS — connect() must not touch
      // these (token refresh also calls connect() and must not re-fire them).
      // isMuted/isReplicaSpeaking are singleton refs — runEndConversation sets
      // isMuted=true to silence the goodbye drain, and only reset() (component
      // unmount) clears it. Without explicit per-session reset, every session
      // that follows a model-initiated end_conversation has mic frames blocked.
      greetingFired = false;
      micUnlocked = false;
      openaiSessionReady = false;
      anamSessionReady = false;
      anamLastSpeechFinishedAt = 0;
      anamSpeechTurnCount = 0;
      anamSpeechResolvers = [];
      isMuted.value = false;
      isReplicaSpeaking.value = false;
      // Orphan every assistant turn from any previous session so a straggling
      // TTS callback cannot speak into this one.
      activeTurnId++;
      ttsPendingText = "";
      ttsChunksSent = 0;
      currentAssistantTurn = "";
      currentUserTurn = "";
      responseInProgress = false;
      currentResponseId = null;
      assistantTurnAbandoned = false;
      textOnlyOutput = true;
      sessionUpdateFellBack = false;
      ttsRawBuffer = "";
      pendingEmotion = null;
      activeEmotion = null;
      directorCuesSupported = true;
      directorCueFailures = 0;
      recentAssistantSpeech = [];
      lastAssistantAudioEndedAt = 0;
      resetAvatarAudio();
      const generation = ++sessionGeneration;
      // Open the TTS socket now so the first thing she says does not also pay
      // for the connection.
      warmCartesia();
      scheduleRefresh();
      await startAnamAvatar();
      await connect();
      await startMic();
      // Unblock mic input immediately so user speech capture is instant
      micUnlocked = true;
      console.log("[assistant] session started; mic unblocked and active");
      // Fallback: if Anam's SESSION_READY never fires (SDK version mismatch,
      // event renamed, etc.), force-fire the greeting after 3s so the user
      // never sits in silence. Tracked + generation-guarded so it cannot fire
      // a greeting into a session that has since been torn down or replaced.
      clearTimeout(greetingFallbackTimer);
      greetingFallbackTimer = setTimeout(() => {
        greetingFallbackTimer = null;
        if (generation !== sessionGeneration || !isActive.value) return;
        if (!anamSessionReady && !greetingFired) {
          console.warn(
            "[assistant] anam SESSION_READY timed out; firing greeting anyway",
          );
          anamSessionReady = true;
          maybeFireGreeting();
        }
      }, 3000);
      return data;
    } catch (e) {
      error.value = friendlyError(e);
      throw e;
    } finally {
      isStarting.value = false;
    }
  }

  async function refreshSession() {
    if (!sessionId.value) return null;
    try {
      const data = await assistantAPI.refreshSession(sessionId.value);
      if (!data) return null;
      ephemeralToken.value = data.ephemeral_token;
      expiresAt.value = data.expires_at;
      scheduleRefresh();
      // Reconnect WSS with fresh token; replay context so the model still knows.
      await reconnect();
      return data;
    } catch (e) {
      error.value = friendlyError(e);
      throw e;
    }
  }

  function applySessionPayload(data) {
    sessionId.value = data.session_id;
    ephemeralToken.value = data.ephemeral_token;
    expiresAt.value = data.expires_at;
    model.value = data.model;
    voice.value = data.voice;
    avatarId.value = data.avatar_id;
    anamSessionToken.value = data.anam_session_token || null;
    systemPrompt.value = data.system_prompt;
    tools.value = Array.isArray(data.tools) ? data.tools : [];
    contextMessages.value = Array.isArray(data.context_messages)
      ? data.context_messages
      : [];
  }

  function setAvatarVideoElementId(id) {
    avatarVideoElementId = id;
  }

  async function startAnamAvatar() {
    if (!anamSessionToken.value || !avatarVideoElementId) return;
    try {
      // The <video> element is mounted by the component only after isActive
      // flips true; wait for it to exist (up to 5s) before handing it to Anam.
      const el = await waitForElement(avatarVideoElementId, 5000);
      if (!el) {
        console.warn("Anam avatar: video element never mounted");
        return;
      }
      const mod = await import("@anam-ai/js-sdk");
      anamClient = mod.createClient(anamSessionToken.value, {
        disableInputAudio: true,
      });
      // Wire SESSION_READY before we start streaming so we never miss it.
      try {
        const evt = mod.AnamEvent && mod.AnamEvent.SESSION_READY;
        if (evt && typeof anamClient.addListener === "function") {
          anamClient.addListener(evt, () => {
            console.log("[assistant] anam SESSION_READY");
            anamSessionReady = true;
            ph("assistant_anam_session_ready");
            maybeFireGreeting();
          });
        }
      } catch (e) {
        console.warn("Anam event wiring failed:", e);
      }

      // CONNECTION_CLOSED resilience. Anam's WebRTC link can drop
      // mid-session (network blip, server-side close, idle timeout)
      // and there's no other signal to the user that the avatar is
      // dead — they just see a frozen video. We log the close to
      // PostHog and attempt one auto-reconnect with a freshly minted
      // Anam session token. If reconnect fails, surface an error so
      // the UI can offer a manual retry.
      try {
        const closedEvt = mod.AnamEvent && mod.AnamEvent.CONNECTION_CLOSED;
        if (closedEvt && typeof anamClient.addListener === "function") {
          // The SDK calls this as (reason: ConnectionClosedCode, details?:
          // string) — reading `.reason` off the first argument logged
          // "unknown" for every disconnect we have ever had.
          const generation = sessionGeneration;
          anamClient.addListener(closedEvt, async (reason, details) => {
            console.warn("[assistant] anam CONNECTION_CLOSED", reason, details);
            ph("assistant_anam_connection_closed", {
              reason: String(reason || "unknown"),
              details: details ? String(details).slice(0, 200) : null,
              reconnect_attempted: anamReconnectInProgress ? false : true,
            });
            // A close belonging to a previous session must never reconnect
            // into the current one — that is how duplicate avatars appear.
            if (generation !== sessionGeneration) return;
            if (anamReconnectInProgress || !isActive.value) return;
            anamReconnectInProgress = true;
            try {
              await reconnectAnam();
              if (generation !== sessionGeneration) return;
              ph("assistant_anam_reconnect_success");
            } catch (err) {
              console.warn("[assistant] anam reconnect failed:", err);
              ph("assistant_anam_reconnect_failed", {
                message: String((err && err.message) || err).slice(0, 200),
              });
              error.value = "Avatar disconnected. Please refresh to reconnect.";
            } finally {
              anamReconnectInProgress = false;
            }
          });
        }
      } catch (e) {
        /* older SDK without CONNECTION_CLOSED */
      }

      // The avatar finishes speaking when MESSAGE_STREAM_EVENT_RECEIVED
      // fires with role='persona' AND endOfSpeech=true. That is the SDK's
      // precise "audio drained for this turn" signal — verified against
      // node_modules/@anam-ai/js-sdk/dist/module/types/messageHistory/MessageStreamEvent.d.ts:
      //   { id, content, role: 'user'|'persona', endOfSpeech, interrupted }.
      try {
        const streamEvt =
          mod.AnamEvent && mod.AnamEvent.MESSAGE_STREAM_EVENT_RECEIVED;
        if (streamEvt && typeof anamClient.addListener === "function") {
          anamClient.addListener(streamEvt, (evt) => {
            const role = evt && evt.role;
            const ended = !!(evt && evt.endOfSpeech);
            // Diagnostic: capture EVERY message-stream event so we can prove
            // in PostHog whether Anam actually fires these in passthrough mode.
            ph("assistant_anam_stream_event", {
              role,
              endOfSpeech: ended,
              interrupted: !!(evt && evt.interrupted),
              content_len: evt && evt.content ? String(evt.content).length : 0,
            });
            if (role === "persona" && ended) {
              anamLastSpeechFinishedAt = Date.now();
              anamSpeechTurnCount += 1;
              if (anamSpeechResolvers.length) {
                const fns = anamSpeechResolvers.splice(0);
                fns.forEach((fn) => {
                  try {
                    fn();
                  } catch (_) {}
                });
              }
            }
          });
        }
      } catch (e) {
        /* older SDK lacks the event — fall through to timeout */
      }

      // Wire the AnalyserNode against Anam's outbound audio MediaStream as
      // soon as it becomes available. This is what runEndConversation uses
      // to know the avatar has actually finished speaking (Anam emits no
      // native "playback drained" event in passthrough mode — confirmed in
      // their docs and SDK source).
      try {
        const audStartedEvt =
          mod.AnamEvent && mod.AnamEvent.AUDIO_STREAM_STARTED;
        if (audStartedEvt && typeof anamClient.addListener === "function") {
          anamClient.addListener(audStartedEvt, async (audioStream) => {
            try {
              if (anamAnalyserCtx) {
                try {
                  anamAnalyserCtx.close();
                } catch (_) {}
              }
              anamAnalyserCtx = new (window.AudioContext ||
                window.webkitAudioContext)();
              // Browsers create AudioContext in `suspended` state when not
              // driven by a user gesture. Without resume(), AnalyserNode
              // reads near-zero and we mistake real audio for silence.
              try {
                await anamAnalyserCtx.resume();
              } catch (_) {}
              const src = anamAnalyserCtx.createMediaStreamSource(audioStream);
              anamAnalyser = anamAnalyserCtx.createAnalyser();
              anamAnalyser.fftSize = 1024;
              anamAnalyser.smoothingTimeConstant = 0.2;
              src.connect(anamAnalyser);
              anamAnalyserData = new Uint8Array(anamAnalyser.fftSize);
              ph("assistant_anam_audio_analyser_ready", {
                ctx_state: anamAnalyserCtx.state,
              });
            } catch (e) {
              console.warn("Anam analyser setup failed:", e);
              ph("assistant_anam_audio_analyser_failed", {
                message: String((e && e.message) || e),
              });
            }
          });
        }
      } catch (e) {
        /* event not present in this SDK version */
      }

      await anamClient.streamToVideoElement(avatarVideoElementId);
      anamAudioStream = anamClient.createAgentAudioInputStream({
        encoding: "pcm_s16le",
        sampleRate: SAMPLE_RATE,
        channels: 1,
      });
    } catch (e) {
      console.warn("Anam avatar setup failed:", e);
      ph("assistant_anam_setup_failed", {
        message: String((e && e.message) || e),
      });
    }
  }

  function waitForElement(id, timeoutMs) {
    return new Promise((resolve) => {
      const found = document.getElementById(id);
      if (found) return resolve(found);
      const start = Date.now();
      const iv = setInterval(() => {
        const el = document.getElementById(id);
        if (el) {
          clearInterval(iv);
          resolve(el);
          return;
        }
        if (Date.now() - start >= timeoutMs) {
          clearInterval(iv);
          resolve(null);
        }
      }, 50);
    });
  }

  async function stopAnamAvatar() {
    try {
      if (anamAudioStream) anamAudioStream.endSequence();
    } catch (_) {}
    try {
      if (anamClient) await anamClient.stopStreaming();
    } catch (_) {}
    try {
      if (anamAnalyserCtx) await anamAnalyserCtx.close();
    } catch (_) {}
    anamAnalyserCtx = null;
    anamAnalyser = null;
    anamAnalyserData = null;
    anamAudioStream = null;
    anamClient = null;
  }

  /**
   * Tear down the existing Anam connection, mint a fresh Anam session
   * token from /assistant/refresh, and reconnect the avatar so the
   * OpenAI Realtime audio stream can resume routing through it.
   * Called from CONNECTION_CLOSED handler. Throws on failure so the
   * caller can show a "please refresh" message.
   */
  async function reconnectAnam() {
    if (!sessionId.value) throw new Error("no session id");
    // An interruption must never come through here — this path is only for a
    // genuinely dead WebRTC link. Invalidate the current turn so the audio
    // that was mid-flight when the link died cannot resume on the new one.
    activeTurnId++;
    if (ttsStream) {
      try {
        ttsStream.cancel();
      } catch (_) {}
      ttsStream = null;
    }
    ttsPendingText = "";
    ttsRawBuffer = "";
    ttsChunksSent = 0;
    pendingEmotion = null;
    activeEmotion = null;
    resetAvatarAudio();
    setAssistantSpeaking(false, { immediate: true });
    await stopAnamAvatar();
    anamSessionReady = false;
    // The new avatar may not be the same model; let cue support re-prove itself.
    directorCuesSupported = true;
    directorCueFailures = 0;
    const data = await assistantAPI.refreshSession(sessionId.value);
    const newAnamToken =
      data &&
      (data.anam_session_token || (data.data && data.data.anam_session_token));
    if (!newAnamToken) {
      throw new Error("refresh did not return a new anam session token");
    }
    anamSessionToken.value = newAnamToken;
    await startAnamAvatar();
  }

  function scheduleRefresh() {
    clearTimeout(refreshTimer);
    if (!expiresAt.value) return;
    const now = Math.floor(Date.now() / 1000);
    const leadSeconds = Math.max(5, Math.min(600, expiresAt.value - now - 60));
    refreshTimer = setTimeout(() => {
      refreshSession().catch((err) =>
        console.warn("Token refresh failed:", err),
      );
    }, leadSeconds * 1000);
  }

  async function saveContext({ final = false, reason = null } = {}) {
    if (!sessionId.value) return null;
    try {
      return await assistantAPI.saveContext(sessionId.value, messages.value, {
        final,
        reason,
      });
    } catch (e) {
      error.value = friendlyError(e);
      throw e;
    }
  }

  function scheduleSaveContext(options = {}) {
    const runSave = () => {
      saveContext(options).catch((err) =>
        console.warn("[assistant] Async context save failed:", err),
      );
    };
    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      window.requestIdleCallback(runSave, { timeout: 2000 });
    } else {
      setTimeout(runSave, 100);
    }
  }

  // -- WSS connect / session.update / context replay ------------------------

  /**
   * Drop the current socket and orphan every listener bound to it. Bumping the
   * generation is what makes that safe: a closing socket delivers its `close`
   * asynchronously, and the old handler used to fire AFTER the replacement had
   * already opened, clearing isConnected and wedging mic capture for the rest
   * of the session (token refresh hit this every time).
   */
  function detachWS() {
    wsGeneration++;
    if (ws) {
      try {
        ws.close();
      } catch (_) {}
      ws = null;
    }
    isConnected.value = false;
  }

  async function connect() {
    if (!ephemeralToken.value || !model.value) return;
    // Never stack sockets — one live socket per session, always.
    detachWS();
    const generation = ++wsGeneration;
    const url = `${REALTIME_WSS_BASE}?model=${encodeURIComponent(model.value)}`;
    const socket = new WebSocket(url, [
      "realtime",
      `openai-insecure-api-key.${ephemeralToken.value}`,
    ]);
    ws = socket;
    const isCurrent = () => generation === wsGeneration && ws === socket;

    await new Promise((resolve, reject) => {
      let settled = false;
      socket.addEventListener(
        "open",
        () => {
          if (!isCurrent()) {
            try {
              socket.close();
            } catch (_) {}
            return;
          }
          isConnected.value = true;
          ph("assistant_wss_opened");
          sendSessionUpdate();
          replayContextMessages();
          // Greeting fires on `session.updated` only when greetingFired is false.
          // startSession() resets it; reconnect() (token refresh) does not.
          settled = true;
          resolve();
        },
        { once: true },
      );
      socket.addEventListener(
        "error",
        (e) => {
          if (!isCurrent()) return;
          ph("assistant_wss_error", {
            message: String((e && e.message) || "unknown"),
          });
          if (!settled) {
            settled = true;
            reject(e);
          }
        },
        { once: true },
      );
      socket.addEventListener("message", (ev) => {
        if (!isCurrent()) return;
        handleWSMessage(ev);
      });
      socket.addEventListener("close", (e) => {
        if (!isCurrent()) return; // stale socket: its state is not ours
        isConnected.value = false;
        ph("assistant_wss_closed", {
          code: e && e.code,
          reason: e && e.reason,
        });
        // The server can reject us purely via the close frame (3003 =
        // policy/config rejection, e.g. model_not_found) without an `error`
        // message ever arriving. Without this the socket dies but the mic,
        // the avatar and the backend session all stay alive burning tokens
        // behind a "disconnected" UI.
        const closeReason = String((e && e.reason) || "");
        if (
          isActive.value &&
          e &&
          e.code === 3003 &&
          /model_not_found|invalid_request_error|invalid_api_key/i.test(
            closeReason,
          )
        ) {
          // The close reason carries the real cause ("invalid_request_error
          // .model_not_found"); pass that through so the user gets the
          // specific message rather than a generic disconnect.
          const derived = /model_not_found/i.test(closeReason)
            ? "model_not_found"
            : /invalid_api_key/i.test(closeReason)
              ? "invalid_api_key"
              : "wss_" + e.code;
          abortSessionOnFatalError(derived, closeReason);
        }
      });
    });
  }

  async function reconnect() {
    detachWS();
    await connect();
  }

  function sendWS(event) {
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify(event));
  }

  function sendSessionUpdate() {
    // Cartesia owns the voice, so OpenAI's own audio is generated, streamed,
    // decoded and then dropped on the floor. Asking for text only removes that
    // work from the critical path: text deltas arrive as fast as the model can
    // produce tokens instead of being paced by audio generation, which is what
    // lets Cartesia start on the first phrase. `error` handling downgrades this
    // automatically if the field is rejected.
    const useTextOnly = useCartesiaTTSMode.value && textOnlyOutput;
    const audio = {
      input: {
        format: { type: "audio/pcm", rate: SAMPLE_RATE },
        transcription: { model: "whisper-1" },
        // threshold stays at 0.5 deliberately. Raising it is the obvious fix
        // for noise triggering VAD and the wrong one — it costs soft speech,
        // which is exactly what we do not want to lose. False positives are
        // handled upstream in pcm-processor.js, which now sends true silence
        // for anything that is not confirmed speech, so the server VAD is
        // never shown room noise in the first place.
        //
        // silence_duration_ms 400 -> 550: 400ms ends the user's turn during an
        // ordinary mid-sentence pause ("I want something for my sister...
        // she likes cooking"), so Ava answers half a thought and the user has
        // to talk over her to finish it. 550ms is still well inside
        // conversational range and costs 150ms of turn latency.
        turn_detection: {
          type: "server_vad",
          threshold: 0.5,
          prefix_padding_ms: 300,
          silence_duration_ms: 550,
          create_response: true,
          interrupt_response: true,
        },
      },
    };
    // Only describe an output voice when OpenAI is actually producing audio.
    if (!useTextOnly) {
      audio.output = {
        format: { type: "audio/pcm", rate: SAMPLE_RATE },
        voice: voice.value,
      };
    }

    sendWS({
      type: "session.update",
      session: {
        type: "realtime",
        model: model.value,
        // Laravel owns what she says; this appends only how she says it.
        instructions: [systemPrompt.value, PERFORMANCE_DIRECTION]
          .filter(Boolean)
          .join("\n\n"),
        tools: tools.value,
        tool_choice: "auto",
        output_modalities: useTextOnly ? ["text"] : ["audio"],
        // Drop ~30% of the conversation window in one shot when we hit
        // the 28,672-token input ceiling, instead of the default
        // micro-truncation on every turn that causes freezing around
        // 75% context fill. Stable behaviour at the cost of forgetting
        // older turns sooner.
        truncation: {
          type: "retention_ratio",
          retention_ratio: 0.7,
        },
        // No max_output_tokens cap. Was 300, but in practice that was
        // truncating responses BEFORE the model could emit the
        // function_call output item — multiple search_gifts misses
        // ended with the model speaking "let me find some gifts..."
        // mid-sentence with no tool call following.
        audio,
      },
    });
  }

  function replayContextMessages() {
    for (const m of contextMessages.value || []) {
      if (!m?.role || !m?.content) continue;
      sendWS({
        type: "conversation.item.create",
        item: {
          type: "message",
          role: m.role,
          content: [
            {
              type: m.role === "assistant" ? "output_text" : "input_text",
              text: String(m.content),
            },
          ],
        },
      });
    }
  }

  // -- assistant turn: TTS + barge-in ---------------------------------------

  /**
   * The ONLY door from TTS to the avatar. A chunk carries the turn it was
   * synthesized for; if that turn has been superseded it is dropped here.
   * Chunks are metered out at roughly real time (see AVATAR_LEAD_MS) rather
   * than dumped as fast as Cartesia produces them.
   */
  function sendAssistantAudioChunk(turnId, b64) {
    if (turnId !== activeTurnId) return;
    if (!b64) return;
    deliverToAvatar(b64);
  }

  /** Duration of a base64 PCM16 mono @24kHz payload, in milliseconds. */
  function base64PcmDurationMs(b64) {
    let chars = b64.length;
    if (b64.endsWith("==")) chars -= 2;
    else if (b64.endsWith("=")) chars -= 1;
    const bytes = Math.floor((chars * 3) / 4);
    return (bytes / 2 / SAMPLE_RATE) * 1000;
  }

  function deliverToAvatar(b64) {
    if (anamAudioStream) {
      try {
        anamAudioStream.sendAudioChunk(b64);
      } catch (e) {
        console.warn("Anam sendAudioChunk failed:", e);
      }
    } else {
      playAudioChunk(b64);
    }
  }

  /**
   * Release queued audio while we are less than AVATAR_LEAD_MS ahead of real
   * time. Note this can only ever DELAY a send, never skip one, so it cannot
   * introduce a gap — if we fall behind, the whole backlog goes at once.
   */
  function pumpAvatarAudio() {
    if (avatarPumpTimer) {
      clearTimeout(avatarPumpTimer);
      avatarPumpTimer = null;
    }
    if (!avatarQueue.length) {
      if (avatarEndPending) finishAvatarSequence();
      return;
    }
    if (!avatarSequenceStartedAt) avatarSequenceStartedAt = Date.now();

    for (;;) {
      if (!avatarQueue.length) break;
      const elapsed = Date.now() - avatarSequenceStartedAt;
      if (avatarSentMs - elapsed >= AVATAR_LEAD_MS) break;
      const chunk = avatarQueue.shift();
      const ms = base64PcmDurationMs(chunk);
      avatarQueuedMs = Math.max(0, avatarQueuedMs - ms);
      avatarSentMs += ms;
      deliverToAvatar(chunk);
    }

    if (avatarQueue.length) {
      avatarPumpTimer = setTimeout(pumpAvatarAudio, AVATAR_PUMP_TICK_MS);
    } else if (avatarEndPending) {
      finishAvatarSequence();
    }
  }

  /**
   * Seconds of audio that exist but have not been heard yet: what we have
   * pushed but Anam has not finished playing, plus what is still queued here.
   * Used to schedule expression cues so the face changes when the matching
   * words are actually spoken, not when the text arrived.
   */
  function avatarPendingSeconds() {
    const elapsed = avatarSequenceStartedAt
      ? Date.now() - avatarSequenceStartedAt
      : 0;
    const unheard = Math.max(0, avatarSentMs - elapsed);
    return (unheard + avatarQueuedMs) / 1000;
  }

  /** Close the Anam sequence once every queued chunk has actually been sent. */
  function finishAvatarSequence() {
    avatarEndPending = false;
    try {
      if (anamAudioStream) anamAudioStream.endSequence();
    } catch (_) {}
    // We have SENT everything, but Anam has not PLAYED everything: metering
    // deliberately keeps us up to AVATAR_LEAD_MS ahead. That remainder is
    // still going to come out of the speakers, so the guard must outlast it.
    const elapsed = avatarSequenceStartedAt
      ? Date.now() - avatarSequenceStartedAt
      : 0;
    const unheardMs = Math.max(0, avatarSentMs - elapsed);
    // Projected wall-clock moment her voice actually stops, not the moment we
    // stopped sending — the echo-transcript window is measured against this.
    lastAssistantAudioEndedAt = Date.now() + unheardMs;
    resetAvatarAudioClock();
    setAssistantSpeaking(false, { tailMs: unheardMs });
  }

  function resetAvatarAudioClock() {
    avatarSequenceStartedAt = 0;
    avatarSentMs = 0;
  }

  /** Barge-in / teardown: drop everything not yet handed to Anam. */
  function resetAvatarAudio() {
    if (avatarPumpTimer) {
      clearTimeout(avatarPumpTimer);
      avatarPumpTimer = null;
    }
    avatarQueue.length = 0;
    avatarQueuedMs = 0;
    avatarEndPending = false;
    resetAvatarAudioClock();
  }

  /**
   * Drive the avatar's expression. Cues are scheduled against the unheard
   * audio so the face moves with the words. Cara 4 avatars only — older
   * avatars reject the cue, so we stop trying after a few failures.
   */
  function dispatchDirectorCue(tag) {
    if (!tag || !directorCuesSupported) return;
    if (!anamClient || typeof anamClient.sendDirectorNoteCue !== "function") {
      return;
    }
    if (typeof anamClient.isStreaming === "function" && !anamClient.isStreaming()) {
      return;
    }
    const lead = avatarPendingSeconds();
    try {
      if (lead > 0.15) {
        anamClient.sendDirectorNoteCue(tag, { inSeconds: Math.min(10, lead) });
      } else {
        anamClient.sendDirectorNoteCue(tag);
      }
      ph("assistant_director_cue", { tag, in_seconds: Math.round(lead * 100) / 100 });
    } catch (e) {
      directorCueFailures += 1;
      if (directorCueFailures >= 3) {
        directorCuesSupported = false;
        console.warn(
          "[assistant] director note cues unsupported on this avatar; disabling",
          e,
        );
        ph("assistant_director_cues_disabled", {
          message: String((e && e.message) || e).slice(0, 200),
        });
      }
    }
  }

  /**
   * Mirror Ava's speaking state into the mic worklet, which raises its gate
   * while she talks so echo residual cannot open it. The release is delayed
   * on a natural end because her audio is still in the WebRTC/output buffer
   * after the last chunk is handed over; a barge-in releases immediately
   * because the user is already talking.
   */
  function setAssistantSpeaking(value, { immediate = false, tailMs = 0 } = {}) {
    if (echoGuardTimer) {
      clearTimeout(echoGuardTimer);
      echoGuardTimer = null;
    }
    if (echoLearnTimer) {
      clearTimeout(echoLearnTimer);
      echoLearnTimer = null;
    }
    isReplicaSpeaking.value = value;
    // if (value) stopHmmReflex();
    const post = (v) => {
      if (!micWorkletNode) return;
      try {
        micWorkletNode.port.postMessage({ type: "assistant-speaking", value: v });
      } catch (_) {}
    };
    if (value) {
      post(true);
      // Tell the gate to measure the echo once her voice is actually audible.
      // TTS start is too early: nothing has reached the speakers yet, so the
      // gate would measure silence and be unprotected when the echo lands.
      echoLearnTimer = setTimeout(() => {
        echoLearnTimer = null;
        if (!micWorkletNode) return;
        try {
          micWorkletNode.port.postMessage({ type: "echo-learn" });
        } catch (_) {}
      }, ECHO_GUARD_PIPELINE_MS);
      return;
    }
    if (immediate) {
      post(false);
      return;
    }
    // Hold the guard across the whole tail. Dropping it while she is still
    // audible is what let her own voice back into the mic — OpenAI's VAD then
    // heard "how are you doing today?", answered it, and she talked over
    // herself. The tail is (audio Anam still has to play) + (pipeline latency).
    post(true);
    echoGuardTimer = setTimeout(
      () => {
        echoGuardTimer = null;
        post(false);
      },
      Math.max(0, tailMs) + ECHO_GUARD_PIPELINE_MS,
    );
  }

  function ensureTTSStream() {
    if (ttsStream && !ttsStream.isCancelled()) return ttsStream;
    const turnId = activeTurnId;
    ttsChunksSent = 0;
    ttsStream = createCartesiaStream({
      onAudioChunk: (b64) => sendAssistantAudioChunk(turnId, b64),
      onStart: () => {
        if (turnId !== activeTurnId) return;
        setAssistantSpeaking(true);
      },
      onDone: () => {
        if (turnId !== activeTurnId) return;
        ttsStream = null;
        // Close the Anam sequence only on a natural end of turn, and only
        // once the pump has actually handed over every queued chunk —
        // ending the sequence with audio still in our own queue would cut
        // her off mid-word. On barge-in the interrupt path owns this.
        avatarEndPending = true;
        pumpAvatarAudio();
      },
      onCancel: () => {
        if (turnId === activeTurnId) ttsStream = null;
      },
      onError: (err) => {
        ph("assistant_tts_failed", {
          message: String((err && err.message) || err).slice(0, 200),
        });
      },
    });
    return ttsStream;
  }

  /**
   * Feed streamed assistant text into TTS, cutting it at natural phrase
   * boundaries. Starting on the first complete phrase rather than on the
   * finished response is the single biggest latency win available here.
   *
   * Inline "[warm]"-style emotion tags are pulled out on the way through, so
   * they drive the avatar's face and the voice's emotion but are never spoken
   * and never reach the transcript we save to Laravel.
   */
  function feedAssistantText(delta) {
    if (!delta) return;
    ttsRawBuffer += delta;

    for (;;) {
      const open = ttsRawBuffer.indexOf("[");
      if (open === -1) break;

      const close = ttsRawBuffer.indexOf("]", open);
      if (close === -1) {
        // A tag may still be arriving. Release everything before it and hold
        // the fragment back; otherwise treat the bracket as literal text.
        if (ttsRawBuffer.length - open <= MAX_EMOTION_TAG_SPAN) {
          emitAssistantText(ttsRawBuffer.slice(0, open));
          ttsRawBuffer = ttsRawBuffer.slice(open);
          return;
        }
        break;
      }

      const tag = ttsRawBuffer.slice(open + 1, close).trim().toLowerCase();
      if (EMOTION_TAGS.indexOf(tag) !== -1) {
        emitAssistantText(ttsRawBuffer.slice(0, open));
        pendingEmotion = tag;
        ttsRawBuffer = ttsRawBuffer.slice(close + 1);
        continue;
      }
      // Not one of ours — pass the bracketed text through untouched.
      emitAssistantText(ttsRawBuffer.slice(0, close + 1));
      ttsRawBuffer = ttsRawBuffer.slice(close + 1);
    }

    emitAssistantText(ttsRawBuffer);
    ttsRawBuffer = "";
  }

  function emitAssistantText(text) {
    if (!text) return;
    currentAssistantTurn += text;
    ttsPendingText += text;
    flushTTSChunks(false);
  }

  function normalizeForEcho(text) {
    return String(text || "")
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  function recordAssistantSpeech(text) {
    const norm = normalizeForEcho(text);
    if (!norm) return;
    recentAssistantSpeech.push({ text: norm, at: Date.now() });
    const cutoff = Date.now() - ECHO_TRANSCRIPT_WINDOW_MS;
    while (recentAssistantSpeech.length && recentAssistantSpeech[0].at < cutoff) {
      recentAssistantSpeech.shift();
    }
  }

  /**
   * Is this "user" transcript actually Ava's own voice returning through the
   * speakers? This is the safety net behind the mic gate: when it slips, the
   * damage is severe — the model answers its own question ("Doing great!"),
   * the fake turn is saved to Laravel, and the polluted context makes her skip
   * her discovery questions on this AND every later session.
   *
   * Only applied while she is speaking or just afterwards, so a user genuinely
   * repeating her words back mid-conversation is never suppressed.
   */
  function isLikelyEchoTranscript(transcript) {
    const inEchoWindow =
      isReplicaSpeaking.value ||
      Date.now() - lastAssistantAudioEndedAt < ECHO_TRANSCRIPT_WINDOW_TAIL_MS;
    if (!inEchoWindow) return false;

    const norm = normalizeForEcho(transcript);
    if (!norm) return true;
    const words = norm.split(" ").filter(Boolean);

    // Whisper invents short stock phrases ("thank you", "bye") out of
    // near-silence; during her turn there is no reason to trust one.
    if (words.length <= 2) return true;

    const cutoff = Date.now() - ECHO_TRANSCRIPT_WINDOW_MS;
    const haystack = recentAssistantSpeech
      .filter((e) => e.at >= cutoff)
      .map((e) => e.text)
      .join(" ");
    if (!haystack) return false;

    if (haystack.indexOf(norm) !== -1) return true; // verbatim fragment

    const spoken = new Set(haystack.split(" "));
    let hits = 0;
    for (let i = 0; i < words.length; i++) {
      if (spoken.has(words[i])) hits++;
    }
    return hits / words.length >= 0.8;
  }

  /**
   * Throw away an echoed turn: stop the reply it triggered, remove the item
   * from the model's conversation, and keep it out of the saved transcript.
   */
  function rejectEchoTranscript(itemId, transcript) {
    console.warn("[assistant] discarding echoed user turn:", transcript);
    ph("assistant_echo_transcript_rejected", {
      transcript: String(transcript || "").slice(0, 120),
    });
    if (itemId) {
      try {
        sendWS({ type: "conversation.item.delete", item_id: itemId });
      } catch (_) {}
    }
    // Delete the echo item from OpenAI conversation history, but DO NOT interrupt
    // Ava's current active speech response!
  }

  /**
   * A light fallback so she still reacts when the model omits a tag. Only
   * consulted for the first phrase of a turn, and only when nothing explicit
   * is pending, so it never overrides the model's own intent.
   */
  function inferEmotion(text) {
    if (/\b(sorry|unfortunately|afraid|couldn't find|no matches)\b/i.test(text)) {
      return "concerned";
    }
    if (/!\s*["')\]]*$/.test(text)) return "happy";
    if (/\?\s*["')\]]*$/.test(text)) return "curious";
    return "warm";
  }

  function flushTTSChunks(force) {
    if (!useCartesiaTTSMode.value) return;
    if (!ttsPendingText && !(force && ttsStream)) return;
    if (!ttsPendingText.trim() && !force) return;
    const stream = ensureTTSStream();
    if (!stream) return;

    for (;;) {
      const minChars =
        ttsChunksSent === 0 ? TTS_FIRST_CHUNK_MIN_CHARS : TTS_CHUNK_MIN_CHARS;
      const cut = speakableChunkLength(ttsPendingText, minChars);
      if (cut <= 0) break;
      const chunk = ttsPendingText.slice(0, cut).trim();
      ttsPendingText = ttsPendingText.slice(cut);
      if (chunk) pushSpokenChunk(stream, chunk);
    }

    if (force) {
      const rest = ttsPendingText.trim();
      ttsPendingText = "";
      if (rest) pushSpokenChunk(stream, rest);
    }
  }

  /**
   * Hand one phrase to TTS, carrying the emotion it should be said with, and
   * move the avatar's face to match when the emotion actually changes.
   */
  function pushSpokenChunk(stream, chunk) {
    if (ttsChunksSent === 0 && !pendingEmotion) {
      pendingEmotion = inferEmotion(chunk);
    }
    if (pendingEmotion && pendingEmotion !== activeEmotion) {
      activeEmotion = pendingEmotion;
      dispatchDirectorCue(activeEmotion);
    }
    pendingEmotion = null;
    recordAssistantSpeech(chunk);
    stream.push(chunk, { emotion: activeEmotion });
    ttsChunksSent++;
  }

  /**
   * End of a complete assistant response: speak the tail and close the turn.
   */
  function finishAssistantTurn() {
    flushTTSChunks(true);
    if (ttsStream) ttsStream.end();
    commitAssistantTurn();
  }

  /**
   * Move whatever Ava has said into the transcript we ship to Laravel.
   * Idempotent — it clears as it commits — so the interrupt path and the
   * transcript-done path can both call it without duplicating a message.
   * Leaving this buffer uncleared was why an interrupted response's text got
   * prepended to the NEXT response and spoken all over again.
   */
  function commitAssistantTurn() {
    const text = currentAssistantTurn.trim();
    currentAssistantTurn = "";
    if (text) messages.value.push({ role: "assistant", content: text });
  }

  /**
   * User started talking over Ava. Everything that could still produce audio
   * for the previous turn is invalidated here, in this order:
   *   1. bump the turn id   - late chunks are now orphaned, synchronously
   *   2. cancel Cartesia    - stops the request and the read loop
   *   3. interruptPersona   - tells Anam to DROP audio it has already buffered
   *   4. endSequence        - closes the orphaned sequence
   *   5. clear local buffers
   * Note the order of 3 and 4: endSequence only marks a sequence finished and
   * resets Anam's chunk counter, it does not discard buffered audio. Calling
   * it alone (as this used to) left the queued audio playing AND reset the
   * counter, so a straggler chunk opened a fresh sequence and Ava resumed.
   */
  function interruptAssistant(reason) {
    activeTurnId++;
    // Deltas for this response are still in flight; drop them on arrival.
    assistantTurnAbandoned = true;
    // stopHmmReflex();

    if (ttsStream) {
      try {
        ttsStream.cancel();
      } catch (_) {}
      ttsStream = null;
    }
    cancelCartesiaTTS();

    try {
      if (anamClient && anamClient.interruptPersona) anamClient.interruptPersona();
    } catch (_) {
      /* SDK throws when not streaming yet — nothing to interrupt then */
    }
    try {
      if (anamAudioStream) anamAudioStream.endSequence();
    } catch (_) {}

    ttsPendingText = "";
    ttsRawBuffer = "";
    ttsChunksSent = 0;
    pendingEmotion = null;
    activeEmotion = null;
    // Anything not yet handed to Anam is discarded outright; what Anam already
    // has was just purged by interruptPersona above.
    resetAvatarAudio();
    lastAssistantAudioEndedAt = Date.now();
    // Drops scheduled Web Audio buffers, clears the native-audio turn timers
    // and releases the echo guard immediately (the user is already talking).
    flushPlayback();
    commitAssistantTurn();

    if (responseInProgress) {
      responseInProgress = false;
      try {
        sendWS({ type: "response.cancel" });
      } catch (_) {}
    }

    ph("assistant_barge_in", { reason: reason || "user_speech" });
  }

  /**
   * True when an output event belongs to a response the user has walked away
   * from. Two independent checks, because either one can be the only signal
   * available: the abandoned flag covers the window between our
   * `response.cancel` and the next `response.created`, and the response_id
   * comparison covers a straggler that arrives after that window closed.
   */
  function isStaleResponseEvent(msg) {
    if (assistantTurnAbandoned) return true;
    if (msg.response_id && currentResponseId) {
      return msg.response_id !== currentResponseId;
    }
    return false;
  }

  // -- inbound event handling -----------------------------------------------

  async function handleWSMessage(ev) {
    let msg;
    try {
      msg = JSON.parse(ev.data);
    } catch (_) {
      return;
    }
    console.debug("[realtime <-]", msg.type, msg.error || "");
    switch (msg.type) {
      case "session.updated":
        console.log("[assistant] openai session.updated");
        openaiSessionReady = true;
        ph("assistant_session_updated");
        maybeFireGreeting();
        break;

      case "response.created":
        currentResponseId = (msg.response && msg.response.id) || null;
        assistantTurnAbandoned = false;
        responseInProgress = true;
        break;

      case "response.done": {
        const doneId = (msg.response && msg.response.id) || null;
        // Ignore a late `done` for a response we already moved on from.
        if (!doneId || !currentResponseId || doneId === currentResponseId) {
          responseInProgress = false;
          // Speak whatever is still buffered and close the turn. Some
          // responses (tool-call-only ones) never emit a transcript.done, so
          // this is the one place guaranteed to run at the end of a response.
          if (!assistantTurnAbandoned) finishAssistantTurn();
        }
        if (!micUnlocked) {
          micUnlocked = true;
          console.log("[assistant] greeting complete; mic unlocked");
          ph("assistant_greeting_complete");
        }
        ph("assistant_response_done", {
          response_id: msg.response && msg.response.id,
          status: msg.response && msg.response.status,
          usage: msg.response && msg.response.usage,
        });
        scheduleSaveContext();
        break;
      }

      case "response.output_audio.delta":
      case "response.audio.delta":
        // When Cartesia TTS mode is active, skip OpenAI native audio to prevent double-voice overlap!
        if (useCartesiaTTSMode.value) break;
        if (isStaleResponseEvent(msg)) break;

        if (msg.delta) {
          if (!replicaTurnStartTs) {
            replicaTurnStartTs = Date.now();
            replicaTurnAudioSamples = 0;
          }
          if (replicaSpeakingTimer) {
            clearTimeout(replicaSpeakingTimer);
            replicaSpeakingTimer = null;
          }
          // Calculate approx sample count from base64 PCM16
          const approxBytes = Math.floor((msg.delta.length * 3) / 4);
          replicaTurnAudioSamples += approxBytes / 2;

          // Same turn-guarded door as the Cartesia path, so a cancelled
          // response's native audio cannot reach the avatar either.
          sendAssistantAudioChunk(activeTurnId, msg.delta);
        }
        setAssistantSpeaking(true);
        break;

      case "response.output_audio.done":
      case "response.audio.done":
        // In Cartesia mode the TTS stream owns endSequence and the speaking
        // state; calling them from here too would close a sequence Cartesia
        // is still filling.
        if (useCartesiaTTSMode.value) break;
        try {
          if (anamAudioStream) anamAudioStream.endSequence();
        } catch (_) {}
        {
          const totalAudioSec = replicaTurnAudioSamples / SAMPLE_RATE;
          const elapsedSec =
            (Date.now() - (replicaTurnStartTs || Date.now())) / 1000;
          const remainingSec = Math.max(0, totalAudioSec - elapsedSec);
          // 450ms safety margin for speaker playback latency, WebRTC buffer & room reverb decay
          const delayMs = Math.round(remainingSec * 1000 + 450);

          if (replicaSpeakingTimer) clearTimeout(replicaSpeakingTimer);
          replicaSpeakingTimer = setTimeout(() => {
            setAssistantSpeaking(false, { immediate: true });
            replicaTurnStartTs = 0;
            replicaTurnAudioSamples = 0;
            replicaSpeakingTimer = null;
          }, delayMs);
        }
        break;

      case "input_audio_buffer.speech_started":
        // Full duplex barge-in. Everything that could still produce audio for
        // the previous turn is invalidated synchronously in here.
        interruptAssistant("speech_started");
        // triggerHmmReflex();
        ph("assistant_user_speech_started");
        break;

      // Streamed assistant text. Whichever modality the session negotiated,
      // these all land in the same phrase-chunked TTS pipeline.
      case "response.output_text.delta":
      case "response.text.delta":
      case "response.output_audio_transcript.delta":
      case "response.audio_transcript.delta":
        if (isStaleResponseEvent(msg)) break;
        // stopHmmReflex();
        feedAssistantText(msg.delta);
        break;

      case "response.output_text.done":
      case "response.text.done":
      case "response.output_audio_transcript.done":
      case "response.audio_transcript.done":
        if (isStaleResponseEvent(msg)) break;
        // Speak the tail immediately rather than waiting for response.done —
        // response.done can trail the last text by a tool call's worth of time.
        finishAssistantTurn();
        break;

      case "conversation.item.input_audio_transcription.delta":
        if (msg.delta) currentUserTurn += msg.delta;
        break;

      case "conversation.item.input_audio_transcription.completed":
        currentUserTurn = "";
        if (isLikelyEchoTranscript(msg.transcript)) {
          rejectEchoTranscript(msg.item_id, msg.transcript);
          break;
        }
        if (msg.transcript) {
          messages.value.push({ role: "user", content: msg.transcript });
        }
        break;

      case "response.function_call_arguments.delta":
        if (msg.name === "search_gifts") {
          isFetchingGifts.value = true;
        }
        break;

      case "response.function_call_arguments.done":
        await handleToolCall(msg);
        break;

      case "error": {
        const apiError = msg.error || {};
        const description = `${apiError.code || ""} ${apiError.message || ""} ${
          apiError.param || ""
        }`;

        // A cancel racing a response that already finished is expected under
        // barge-in and is not worth surfacing.
        if (/no active response/i.test(description)) break;

        // If this deployment rejects the text-only output modality, fall back
        // to the audio session once and re-send. Without this a rejected
        // session.update means no `session.updated`, no greeting, dead session.
        if (
          !sessionUpdateFellBack &&
          textOnlyOutput &&
          /modalit/i.test(description)
        ) {
          sessionUpdateFellBack = true;
          textOnlyOutput = false;
          console.warn(
            "[assistant] text-only output rejected; retrying session.update with audio",
          );
          ph("assistant_output_modality_fallback");
          sendSessionUpdate();
          break;
        }

        console.warn("OpenAI Realtime error:", msg.error || msg);
        ph("assistant_realtime_error", { error: msg.error || msg });

        // Unrecoverable server-side rejections (bad/unavailable model, revoked
        // or expired key, org without Realtime access) never resolve by
        // waiting: the socket is about to close and every retry re-mints a
        // token. Previously we only logged, so the mic kept capturing and the
        // session stayed billable in the background with the UI showing
        // "disconnected". Abort the whole session once, for real.
        if (isFatalRealtimeError(apiError)) {
          abortSessionOnFatalError(
            apiError.code || "realtime_error",
            apiError.message || "",
          );
        }
        break;
      }
    }
  }

  // -- client → server helpers ---------------------------------------------

  function pushEcho(text) {
    sendWS({
      type: "conversation.item.create",
      item: {
        type: "message",
        role: "assistant",
        content: [{ type: "output_text", text }],
      },
    });
  }

  function appendLLMContext(text) {
    sendWS({
      type: "conversation.item.create",
      item: {
        type: "message",
        role: "assistant",
        content: [{ type: "output_text", text }],
      },
    });
  }

  function respondToToolCall(callId, resultText) {
    sendWS({
      type: "conversation.item.create",
      item: {
        type: "function_call_output",
        call_id: callId,
        output: resultText,
      },
    });
    sendWS({ type: "response.create" });
  }

  // -- tool-call router ----------------------------------------------------

  async function handleToolCall(msg) {
    const name = msg.name;
    const callId = msg.call_id;
    let args = {};
    try {
      args = JSON.parse(msg.arguments || "{}");
    } catch (_) {}

    const startedAt = Date.now();
    ph("assistant_tool_called", { tool: name, call_id: callId, args });

    emitToolCall({ name, args, callId });

    let result = "unknown_tool";
    let error = null;
    try {
      if (name === "search_gifts") {
        await runSearchGifts(callId, args.query || "");
        result = "ok";
      } else if (name === "end_conversation") {
        await runEndConversation(callId, args.reason || "satisfied");
        result = "ok";
      } else {
        respondToToolCall(callId, JSON.stringify({ status: "unknown_tool" }));
      }
    } catch (e) {
      error = String((e && e.message) || e);
      result = "error";
    } finally {
      ph("assistant_tool_completed", {
        tool: name,
        call_id: callId,
        result,
        error,
        latency_ms: Date.now() - startedAt,
      });
    }
  }

  async function runSearchGifts(callId, query) {
    pushEcho(ECHO_FILLER_PRIMARY);
    isFetchingGifts.value = true;
    gifts.value = [];

    let slowTimer = setTimeout(() => {
      pushEcho(ECHO_FILLER_SLOW);
    }, SLOW_SEARCH_MS);

    try {
      const data = await giftAPI.searchGifts(query || "");
      trackUserEvent({
        eventType: "assistant_search",
        keyword: query || "",
      });
      const rawItems =
        data?.response?.item || data?.items || data?.response?.items || [];
      const items = rawItems.map((item, index) => ({
        ...item,
        productId:
          item.productId ||
          item.product_id ||
          item.id ||
          item.sku ||
          `gift-${index}`,
        orderIndex: item.orderIndex !== undefined ? item.orderIndex : index,
        searchQuery: query || "",
      }));
      gifts.value = items;

      const summary = items.length
        ? `Found ${items.length} gift options centered on the user's query. The grid is now shown on screen.`
        : "No matching gifts found for that query.";
      appendLLMContext(summary);
      respondToToolCall(
        callId,
        JSON.stringify({
          result_count: items.length,
          summary,
        }),
      );
    } catch (e) {
      console.warn("search_gifts failed:", e);
      respondToToolCall(
        callId,
        JSON.stringify({ error: e?.message || "search failed" }),
      );
    } finally {
      clearTimeout(slowTimer);
      isFetchingGifts.value = false;
    }
  }

  async function runEndConversation(callId, reason) {
    ph("assistant_session_ended", { reason, via: "end_conversation_tool" });

    // Acknowledge the tool call. respondToToolCall fires `response.create`
    // which causes the model to generate the goodbye audio, which then
    // streams through Anam's playback pipeline.
    respondToToolCall(callId, JSON.stringify({ acknowledged: true }));

    // Stop sending mic frames immediately so the user can't trigger another
    // turn while we're closing.
    isMuted.value = true;
    // Release the microphone hardware too. isMuted only gates forwarding;
    // the MediaStream stays live and the browser keeps its recording
    // indicator lit. This path then waits on the goodbye audio drain (up
    // to 15s), so without this the mic sat visibly open that whole time.
    // Safe here: the user's turn is over — everything after this point is
    // Ava speaking her goodbye, which needs playback, not capture.
    stopMic();
    stopAnamMicCapture();
    try {
      await saveContext({ final: true, reason });
    } catch (_) {
      /* don't block the goodbye on a save error */
    }

    // Wait for Anam's actual audio output (the MediaStream that's playing
    // through the user's speakers) to go quiet. We listen via an
    // AnalyserNode wired to that MediaStream — see startAnamAvatar's
    // AUDIO_STREAM_STARTED handler. This is the "natively correct" signal
    // Anam doesn't expose as an event in passthrough mode: rather than
    // guessing with a timer, we monitor the speaker output directly.
    const waitStart = Date.now();
    ph("assistant_end_wait_started", {
      ctx_state: anamAnalyserCtx ? anamAnalyserCtx.state : "none",
    });
    const result = await waitForPersonaAudioSilence({
      silenceMs: 1200,
      maxMs: 15000,
      threshold: 3,
    });
    ph("assistant_end_wait_resolved", {
      waited_ms: Date.now() - waitStart,
      reason: result.reason, // 'silence' | 'timeout' | 'no_analyser'
      peak_rms: result.peakRms,
      heard_audio: result.heardAudio,
    });

    await teardown();
    storage.setLastConversationEndTime();
    isActive.value = false;
    phClearSession();
  }

  /**
   * Resolves when Anam's outbound audio has been continuously silent for
   * `silenceMs` after we've heard at least some audio activity. Falls back
   * to `maxMs` if we either never hear audio or never see silence.
   *
   * Returns 'silence' on normal resolution, 'timeout' on hard cap, or
   * 'no_analyser' if the AnalyserNode wasn't ready (we couldn't latch onto
   * Anam's MediaStream).
   */
  function waitForPersonaAudioSilence({
    silenceMs = 1200,
    maxMs = 15000,
    threshold = 3,
  } = {}) {
    return new Promise((resolve) => {
      if (!anamAnalyser || !anamAnalyserData) {
        setTimeout(
          () =>
            resolve({ reason: "no_analyser", peakRms: 0, heardAudio: false }),
          Math.min(2000, maxMs),
        );
        return;
      }
      // If the analyser context is suspended (autoplay policy), try to
      // resume it before we start polling — otherwise we read silence.
      if (anamAnalyserCtx && anamAnalyserCtx.state === "suspended") {
        try {
          anamAnalyserCtx.resume();
        } catch (_) {}
      }
      const start = Date.now();
      let silentSince = null;
      let heardAudio = false;
      let peakRms = 0;
      const tick = () => {
        try {
          anamAnalyser.getByteTimeDomainData(anamAnalyserData);
        } catch (_) {
          resolve({ reason: "no_analyser", peakRms, heardAudio });
          return;
        }
        let sum = 0;
        for (let i = 0; i < anamAnalyserData.length; i++) {
          const d = anamAnalyserData[i] - 128;
          sum += d * d;
        }
        const rms = Math.sqrt(sum / anamAnalyserData.length);
        if (rms > peakRms) peakRms = rms;
        const now = Date.now();
        if (rms >= threshold) {
          heardAudio = true;
          silentSince = null;
        } else if (heardAudio) {
          if (silentSince === null) silentSince = now;
          if (now - silentSince >= silenceMs) {
            resolve({ reason: "silence", peakRms, heardAudio });
            return;
          }
        }
        if (now - start >= maxMs) {
          resolve({ reason: "timeout", peakRms, heardAudio });
          return;
        }
        setTimeout(tick, 30);
      };
      tick();
    });
  }

  /**
   * Resolves on the NEXT persona-turn-end signal from Anam, or after maxMs.
   * Captured at the call site so a turn that ended *before* this is called
   * doesn't satisfy the wait.
   */
  function waitForAnamPersonaTurnEnd({ maxMs = 15000 } = {}) {
    return new Promise((resolve) => {
      let settled = false;
      let timer = null;
      const finish = () => {
        if (settled) return;
        settled = true;
        if (timer) {
          clearTimeout(timer);
          timer = null;
        }
        const idx = anamSpeechResolvers.indexOf(finish);
        if (idx !== -1) anamSpeechResolvers.splice(idx, 1);
        resolve();
      };
      anamSpeechResolvers.push(finish);
      timer = setTimeout(finish, maxMs);
    });
  }

  // -- Web Audio: mic capture ----------------------------------------------

  async function startMic() {
    try {
      micStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          // Keep AEC on: Ava's voice coming back through the speakers is a
          // first-class source of false VAD triggers, and this is the only
          // layer that can remove it before it reaches us.
          echoCancellation: true,
          // Keep NS on: it strips stationary noise, which keeps the adaptive
          // floor in pcm-processor.js low and therefore keeps the gate
          // sensitive to soft speech.
          noiseSuppression: true,
          // OFF, and this matters. AGC raises mic gain when the room is quiet,
          // which lifts fan/AC hum toward speech level — it is actively
          // fighting a level-based gate, and it makes the noise floor
          // non-stationary so nothing downstream can track it. With AGC off,
          // quiet stays quiet and speech stays clearly above it.
          autoGainControl: false,
          channelCount: 1,
        },
      });
      audioCtx = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: SAMPLE_RATE,
      });
      micSource = audioCtx.createMediaStreamSource(micStream);

      let workletLoaded = false;
      if (audioCtx.audioWorklet) {
        try {
          await audioCtx.audioWorklet.addModule("/pcm-processor.js");
          micWorkletNode = new AudioWorkletNode(audioCtx, "pcm-processor");
          micWorkletNode.port.onmessage = (ev) => {
            const data = ev && ev.data;
            if (!data) return;
            if (data.type === "stats") {
              console.debug("[mic gate]", data);
              return;
            }
            if (!micUnlocked) return;
            if (
              isMuted.value ||
              !isConnected.value ||
              !ws ||
              ws.readyState !== WebSocket.OPEN
            )
              return;

            if (data.pcm16) {
              sendWS({
                type: "input_audio_buffer.append",
                audio: bufToBase64(data.pcm16),
              });
            }
          };
          micSource.connect(micWorkletNode);
          // Route to a muted gain node rather than straight to the speakers.
          // The worklet emits no audio of its own, but connecting a live mic
          // path to `destination` is how a feedback loop gets built by
          // accident the moment anything downstream changes.
          const micSink = audioCtx.createGain();
          micSink.gain.value = 0;
          micWorkletNode.connect(micSink);
          micSink.connect(audioCtx.destination);
          // Carry the current speaking state over to a freshly created worklet
          // (mic restart mid-turn).
          try {
            micWorkletNode.port.postMessage({
              type: "assistant-speaking",
              value: isReplicaSpeaking.value,
            });
          } catch (_) {}
          workletLoaded = true;
          console.log(
            "[assistant] AudioWorklet background thread mic capture active",
          );
        } catch (e) {
          console.warn(
            "[assistant] AudioWorklet load failed, using ScriptProcessor fallback:",
            e,
          );
        }
      }

      if (!workletLoaded) {
        micProcessor = audioCtx.createScriptProcessor(2048, 1, 1);
        micProcessor.onaudioprocess = (ev) => {
          if (!micUnlocked) return;
          if (
            isMuted.value ||
            !isConnected.value ||
            !ws ||
            ws.readyState !== WebSocket.OPEN
          )
            return;

          const input = ev.inputBuffer.getChannelData(0);
          // Pass the VIEW, not `.buffer` — floatToPCM16 returns a window onto
          // a larger reused cache and serialising the whole cache appended
          // stale samples to every frame.
          sendWS({
            type: "input_audio_buffer.append",
            audio: bufToBase64(floatToPCM16(input)),
          });
        };
        micSource.connect(micProcessor);
        micProcessor.connect(audioCtx.destination);
        console.log(
          "[assistant] ScriptProcessor mic capture active (fallback)",
        );
      }
    } catch (e) {
      error.value = "Microphone access denied or unavailable.";
      throw e;
    }
  }

  /**
   * Release the microphone the Anam SDK opened for itself.
   *
   * We run two capture paths: our own getUserMedia stream (stopMic) and the
   * one Anam's StreamingClient opens internally for WebRTC. Anam only frees
   * its stream inside shutdown(), which sits behind `await stopStreaming()`
   * — a full WebRTC + signalling teardown that takes seconds. Until it
   * finished the browser kept showing the recording indicator even though
   * our own mic was long gone.
   *
   * So reach in and stop Anam's input tracks directly, synchronously. The
   * SDK exposes no public API for this (muteInputAudio only flips
   * track.enabled, which does NOT clear the indicator), hence the guarded
   * property walk: every step is optional-chained and wrapped, so an SDK
   * upgrade that renames internals degrades to the old timing rather than
   * throwing. stopStreaming() still runs afterwards and is idempotent
   * against already-stopped tracks.
   */
  function stopAnamMicCapture() {
    try {
      const sc = anamClient && anamClient.streamingClient;
      const stream = sc && sc.inputAudioStream;
      if (stream && typeof stream.getTracks === "function") {
        stream.getTracks().forEach((t) => {
          try {
            t.stop();
          } catch (_) {}
        });
      }
    } catch (_) {
      /* SDK internals changed: fall back to stopStreaming()'s own cleanup */
    }
  }

  function stopMic() {
    try {
      if (micWorkletNode) {
        // Drop the port handler explicitly: the node keeps the closure (and
        // through it this whole session) alive otherwise.
        micWorkletNode.port.onmessage = null;
        try {
          micWorkletNode.port.close();
        } catch (_) {}
        micWorkletNode.disconnect();
      }
    } catch (_) {}
    try {
      if (micProcessor) micProcessor.onaudioprocess = null;
    } catch (_) {}
    try {
      micProcessor && micProcessor.disconnect();
    } catch (_) {}
    try {
      micSource && micSource.disconnect();
    } catch (_) {}
    try {
      audioCtx && audioCtx.close();
    } catch (_) {}
    if (micStream) micStream.getTracks().forEach((t) => t.stop());
    micWorkletNode = null;
    micProcessor = null;
    micSource = null;
    audioCtx = null;
    micStream = null;
  }

  function toggleMute() {
    isMuted.value = !isMuted.value;
  }

  // -- Web Audio: playback --------------------------------------------------

  function playAudioChunk(b64) {
    if (!b64) return;
    if (!playbackCtx) {
      playbackCtx = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: SAMPLE_RATE,
      });
      playbackQueueTime = playbackCtx.currentTime;
    }
    // Bounded schedule-ahead. Without this a long turn (or a suspended
    // context) accumulates scheduled buffers with nothing to drain them.
    if (playbackQueueTime - playbackCtx.currentTime > MAX_PLAYBACK_QUEUE_SEC) {
      return;
    }
    const bytes = base64ToBytes(b64);
    const floats = pcm16ToFloat32(bytes);
    const buffer = playbackCtx.createBuffer(1, floats.length, SAMPLE_RATE);
    buffer.copyToChannel(floats, 0);
    const src = playbackCtx.createBufferSource();
    src.buffer = buffer;
    src.connect(playbackCtx.destination);
    // Tracked so a barge-in can stop what is already scheduled; untracked
    // sources keep playing no matter what the rest of the pipeline does.
    playbackSources.add(src);
    src.onended = () => {
      playbackSources.delete(src);
      try {
        src.disconnect();
      } catch (_) {}
    };
    const start = Math.max(playbackCtx.currentTime, playbackQueueTime);
    src.start(start);
    playbackQueueTime = start + buffer.duration;
  }

  /**
   * Stop and release every scheduled buffer, keeping the AudioContext alive.
   * Closing and recreating the context per interruption (what this used to do)
   * churns a scarce resource — browsers cap concurrent AudioContexts — and
   * costs several ms of setup on the interrupt path.
   */
  function stopPlayback() {
    playbackSources.forEach((src) => {
      try {
        src.onended = null;
        src.stop();
      } catch (_) {}
      try {
        src.disconnect();
      } catch (_) {}
    });
    playbackSources.clear();
    playbackQueueTime = playbackCtx ? playbackCtx.currentTime : 0;
  }

  function flushPlayback() {
    cancelCartesiaTTS();
    if (replicaSpeakingTimer) {
      clearTimeout(replicaSpeakingTimer);
      replicaSpeakingTimer = null;
    }
    replicaTurnStartTs = 0;
    replicaTurnAudioSamples = 0;
    setAssistantSpeaking(false, { immediate: true });
    stopPlayback();
  }

  function closePlayback() {
    stopPlayback();
    try {
      playbackCtx && playbackCtx.close();
    } catch (_) {}
    playbackCtx = null;
    playbackQueueTime = 0;
  }

  // -- teardown -------------------------------------------------------------

  async function teardown() {
    // Invalidate first: after this point no in-flight callback from this
    // session can reach the avatar, the socket, or the next session.
    sessionGeneration++;
    activeTurnId++;
    if (ttsStream) {
      try {
        ttsStream.cancel();
      } catch (_) {}
      ttsStream = null;
    }
    closeCartesiaTransport();
    ttsPendingText = "";
    ttsRawBuffer = "";
    ttsChunksSent = 0;
    pendingEmotion = null;
    activeEmotion = null;
    currentAssistantTurn = "";
    currentUserTurn = "";
    responseInProgress = false;
    currentResponseId = null;
    assistantTurnAbandoned = false;
    recentAssistantSpeech = [];
    lastAssistantAudioEndedAt = 0;
    resetAvatarAudio();

    clearTimeout(refreshTimer);
    refreshTimer = null;
    clearTimeout(greetingFallbackTimer);
    greetingFallbackTimer = null;
    if (echoGuardTimer) {
      clearTimeout(echoGuardTimer);
      echoGuardTimer = null;
    }
    if (echoLearnTimer) {
      clearTimeout(echoLearnTimer);
      echoLearnTimer = null;
    }
    if (replicaSpeakingTimer) {
      clearTimeout(replicaSpeakingTimer);
      replicaSpeakingTimer = null;
    }
    // Any waiter parked on an Anam persona-turn-end must not outlive the
    // session it was created for.
    anamSpeechResolvers.splice(0).forEach((fn) => {
      try {
        fn();
      } catch (_) {}
    });

    stopMic();
    // Before the await: stopAnamAvatar() runs a full WebRTC teardown and
    // only frees Anam's mic at the end of it.
    stopAnamMicCapture();
    closePlayback();
    isReplicaSpeaking.value = false;
    await stopAnamAvatar();
    detachWS();
  }

  /**
   * True for OpenAI Realtime errors that cannot be recovered by retrying:
   * the model is wrong or unavailable to this org, or the credential is
   * bad. Anything transient (rate limit, temporary server fault) is
   * deliberately excluded so we don't kill a session that would recover.
   */
  function isFatalRealtimeError(apiError) {
    const code = String((apiError && apiError.code) || "").toLowerCase();
    const type = String((apiError && apiError.type) || "").toLowerCase();
    if (
      code === "model_not_found" ||
      code === "invalid_api_key" ||
      code === "insufficient_quota" ||
      code === "account_deactivated"
    ) {
      return true;
    }
    // An invalid_request_error naming the model is the same class of
    // problem even when the code field differs between API versions.
    return (
      type === "invalid_request_error" &&
      /\bmodel\b/i.test(String((apiError && apiError.message) || ""))
    );
  }

  /**
   * Message shown when a session is killed by an unrecoverable error.
   * Each case names the actual cause so whoever sees it knows what to do:
   * top up billing, fix the model config, or rotate the key. Kept free of
   * OpenAI's raw wording, which is too internal to put in front of a user.
   */
  function fatalErrorMessage(code) {
    switch (String(code || "").toLowerCase()) {
      case "insufficient_quota":
        return "Ava is out of OpenAI credits. Please top up the OpenAI account billing to continue.";
      case "invalid_api_key":
        return "Ava's OpenAI API key is invalid or expired. Please update the API key.";
      case "account_deactivated":
        return "The OpenAI account has been deactivated. Please check the OpenAI account status.";
      case "model_not_found":
        return "Ava's AI model is unavailable or not enabled for this OpenAI account. Please check the model configuration.";
      default:
        return "Ava was disconnected unexpectedly. Please try again.";
    }
  }

  // Guard so the error frame and the socket `close` that follows it don't
  // both run teardown (and don't both POST a final save-context).
  let fatalAbortInProgress = false;

  /**
   * Tear a session down after an unrecoverable server error: stop the mic,
   * stop the avatar, close the socket, and tell the backend the session is
   * over so it is not left open and accruing tokens.
   */
  async function abortSessionOnFatalError(code, message) {
    if (fatalAbortInProgress) return;
    if (!isActive.value && !isConnected.value) return;
    fatalAbortInProgress = true;
    console.warn("[assistant] fatal realtime error; aborting session:", code);
    ph("assistant_session_ended", {
      reason: "fatal_realtime_error",
      via: "abort_on_fatal_error",
      code,
    });
    // Everything the UI reads is set SYNCHRONOUSLY, before the first await.
    // Setting error last meant the whole save+teardown ran while the UI
    // still showed 'connected' and then 'disconnected' — the user saw the
    // avatar panel and controls flash before the message appeared. Going to
    // the error state first makes the transition a single frame.
    isMuted.value = true;
    isConnected.value = false;
    isActive.value = false;
    error.value = fatalErrorMessage(code);
    // Release the microphone RIGHT NOW, before any await. isMuted only stops
    // us forwarding frames — the MediaStream tracks stay live, so the browser
    // keeps showing its "mic in use" indicator. saveContext() is a network
    // round-trip and stopAnamAvatar() tears down WebRTC, so leaving the mic
    // to teardown() held it open for seconds after the error was on screen.
    stopMic();
    stopAnamMicCapture();
    try {
      await saveContext({ final: true, reason: `fatal:${code}` });
    } catch (_) {
      /* never block teardown on the save */
    }
    try {
      await teardown();
    } catch (_) {
      /* best effort */
    }
    storage.setLastConversationEndTime();
    phClearSession();
    // Re-assert the message: teardown()/stopAnamAvatar() can surface their
    // own errors while unwinding, and the user must still see the real
    // cause rather than whatever failed last on the way out.
    error.value = fatalErrorMessage(code);
    fatalAbortInProgress = false;
  }

  async function leaveKeepingGifts() {
    ph("assistant_session_ended", {
      reason: "user_left",
      via: "leave_keeping_gifts",
    });
    // Same reason as the fatal-error path: drop the mic before the
    // save round-trip so the browser's recording indicator clears the
    // moment the user clicks Leave, not seconds later.
    isMuted.value = true;
    stopMic();
    stopAnamMicCapture();
    try {
      await saveContext({ final: true, reason: "user_left" });
    } catch (_) {}
    await teardown();
    storage.setLastConversationEndTime();
    isActive.value = false;
    phClearSession();
  }

  function reset() {
    teardown();
    sessionId.value = null;
    ephemeralToken.value = null;
    expiresAt.value = null;
    model.value = null;
    voice.value = null;
    avatarId.value = null;
    systemPrompt.value = null;
    tools.value = [];
    contextMessages.value = [];
    messages.value = [];
    gifts.value = [];
    isActive.value = false;
    isStarting.value = false;
    isMuted.value = false;
    isReplicaSpeaking.value = false;
    error.value = null;
    onToolCallCallbacks = [];
    greetingFired = false;
    micUnlocked = false;
    openaiSessionReady = false;
    anamSessionReady = false;
    ttsPendingText = "";
    ttsRawBuffer = "";
    ttsChunksSent = 0;
    pendingEmotion = null;
    activeEmotion = null;
    currentAssistantTurn = "";
    currentUserTurn = "";
    responseInProgress = false;
    currentResponseId = null;
    assistantTurnAbandoned = false;
    textOnlyOutput = true;
    sessionUpdateFellBack = false;
    directorCuesSupported = true;
    directorCueFailures = 0;
    recentAssistantSpeech = [];
    lastAssistantAudioEndedAt = 0;
    resetAvatarAudio();
  }

  /**
   * Clear a surfaced fatal error so the UI can return to the start button.
   * The session is already torn down at this point; only the message is
   * being dismissed.
   */
  function clearError() {
    error.value = null;
  }

  function friendlyError(e) {
    return e?.response?.data?.message || e?.message || String(e);
  }

  return {
    // state
    sessionId,
    ephemeralToken,
    expiresAt,
    model,
    voice,
    avatarId,
    systemPrompt,
    tools,
    contextMessages,
    messages,
    gifts,
    isFetchingGifts,
    isActive,
    isConnected,
    isStarting,
    isMuted,
    isReplicaSpeaking,
    error,
    // actions
    startSession,
    refreshSession,
    toggleMute,
    leaveKeepingGifts,
    reset,
    onToolCall,
    setAvatarVideoElementId,
    // expose for the component to trigger manual final save etc.
    saveContext,
    clearError,
  };
}
